/**
 * autoFitWidth.ts — the ONE content-based column-width calculator.
 *
 * Used by the shared `<AppDataGrid>` (a column's `autoFit` option) AND by the few
 * hand-rolled grids that mirror it (the SDMS batch-results table), so an auto-fit
 * column sizes identically everywhere (RTCFR "Dynamic Permit Limit Column Width",
 * 2026-09-27). Width = the longest visible text line OR the header — whichever is
 * wider — plus the cell / header chrome, clamped to [min, max]. Content past `max`
 * wraps inside the cell (the caller opts the cell into wrapping) and keeps a tooltip.
 */

/** Min / max bounds of an auto-fit column, in px. */
export interface AutoFitBounds {
  min: number;
  max: number;
}

/** Everything the calculator needs; all widths in px. */
export interface AutoFitInput extends AutoFitBounds {
  /** The header's plain-text label. */
  header: string;
  /** Every visible text LINE in the column (a stacked cell contributes one entry per line). */
  lines: readonly string[];
  /** CSS `font` shorthand the cells render in, e.g. "400 12.5px Inter". */
  font: string;
  /** CSS `font` the header renders in (headers are bold); defaults to `font`. */
  headerFont?: string;
  /** Horizontal cell chrome: left + right padding + border. */
  cellChrome: number;
  /** Horizontal header chrome: padding + the sort caret + the filter chevron + resize handle. */
  headerChrome: number;
}

/** Standard chrome (px) every auto-fit column adds to its measured text — shared so
 *  the AppDataGrid and the hand-rolled grids size identically. Body cell = left +
 *  right padding + border + rounding slack; header = padding + sort caret + filter
 *  chevron + resize-handle slack. */
export const AUTOFIT_CELL_CHROME = 28;
export const AUTOFIT_HEADER_CHROME = 64;

/** Fallback font when the grid's computed font is not known yet (first paint / tests). */
export const DEFAULT_GRID_FONT = "400 12.5px system-ui, sans-serif";

let canvasCtx: CanvasRenderingContext2D | null | undefined;

/** The px width of `text` in `font`. Canvas when available; a per-character
 *  estimate otherwise (jsdom has no canvas) so the result is never 0. */
export function measureTextWidth(text: string, font: string): number {
  if (!text) return 0;
  if (canvasCtx === undefined) {
    // jsdom (unit tests) has no canvas and logs "not implemented" if asked — skip it.
    const noCanvas =
      typeof document === "undefined" ||
      (typeof navigator !== "undefined" && /jsdom/i.test(navigator.userAgent));
    canvasCtx = noCanvas ? null : document.createElement("canvas").getContext("2d");
  }
  if (canvasCtx) {
    canvasCtx.font = font;
    const w = canvasCtx.measureText(text).width;
    if (w > 0) return w;
  }
  const px = /(\d+(?:\.\d+)?)px/.exec(font);
  const size = px ? Number(px[1]) : 12.5;
  return text.length * size * 0.6;
}

/** The auto-fit width of one column: max(header, longest line) + chrome, clamped to [min, max]. */
export function autoFitWidth(input: AutoFitInput): number {
  const headerW = measureTextWidth(input.header, input.headerFont ?? input.font) + input.headerChrome;
  let longest = 0;
  for (const line of input.lines) {
    const w = measureTextWidth(line, input.font);
    if (w > longest) longest = w;
  }
  const contentW = longest > 0 ? longest + input.cellChrome : 0;
  const raw = Math.ceil(Math.max(headerW, contentW));
  return Math.min(input.max, Math.max(input.min, raw));
}

/** The same font at weight 700 — the shared grid header weight. */
export function boldFont(font: string): string {
  return /^\S+ \d{3} /.test(font) ? font.replace(/^(\S+) \d{3} /, "$1 700 ") : `700 ${font}`;
}

/** The CSS `font` shorthand an element renders in (for `autoFitWidth`). */
export function fontOf(el: Element | null | undefined): string {
  if (!el || typeof getComputedStyle === "undefined") return DEFAULT_GRID_FONT;
  const cs = getComputedStyle(el);
  if (!cs.fontSize) return DEFAULT_GRID_FONT;
  return `${cs.fontStyle || "normal"} ${cs.fontWeight || "400"} ${cs.fontSize} ${cs.fontFamily || "sans-serif"}`;
}
